import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, AlertCircle, Info, Construction } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { useActiveSystemAnnouncements } from '@/hooks/useSystemAnnouncements';
import { useAuth } from '@/lib/AuthContext';

const TYPE_CONFIG = {
  info: {
    icon: Info,
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
    borderColor: 'border-blue-600'
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-500',
    textColor: 'text-yellow-900',
    borderColor: 'border-yellow-600'
  },
  maintenance: {
    icon: Construction,
    bgColor: 'bg-orange-500',
    textColor: 'text-white',
    borderColor: 'border-orange-600'
  },
  critical: {
    icon: AlertCircle,
    bgColor: 'bg-red-500',
    textColor: 'text-white',
    borderColor: 'border-red-600'
  }
};

export default function SystemAnnouncementBanner() {
  const { userProfile } = useAuth();
  const userRole = userProfile?.active_role || userProfile?.primary_role;

  const { data: announcements = [] } = useActiveSystemAnnouncements(userRole, {
    enabled: !!userRole
  });

  // Track dismissed banners in localStorage
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const stored = localStorage.getItem('dismissedAnnouncements');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Track acknowledged blocking modals in sessionStorage (per session)
  const [acknowledgedIds, setAcknowledgedIds] = useState(() => {
    try {
      const stored = sessionStorage.getItem('acknowledgedAnnouncements');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Filter announcements
  const bannerAnnouncements = announcements.filter(
    a => !a.is_blocking && !dismissedIds.includes(a.id)
  );

  const blockingAnnouncements = announcements.filter(
    a => a.is_blocking && !acknowledgedIds.includes(a.id)
  );

  // Show the first blocking announcement as a modal
  const blockingAnnouncement = blockingAnnouncements[0];

  const dismissBanner = (id) => {
    const newDismissed = [...dismissedIds, id];
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
  };

  const acknowledgeBlocking = (id) => {
    const newAcknowledged = [...acknowledgedIds, id];
    setAcknowledgedIds(newAcknowledged);
    sessionStorage.setItem('acknowledgedAnnouncements', JSON.stringify(newAcknowledged));
  };

  // Clean up old dismissed IDs that are no longer active
  useEffect(() => {
    const activeIds = announcements.map(a => a.id);
    const stillValid = dismissedIds.filter(id => activeIds.includes(id));
    if (stillValid.length !== dismissedIds.length) {
      setDismissedIds(stillValid);
      localStorage.setItem('dismissedAnnouncements', JSON.stringify(stillValid));
    }
  }, [announcements]);

  if (bannerAnnouncements.length === 0 && !blockingAnnouncement) {
    return null;
  }

  return (
    <>
      {/* Top Banners */}
      {bannerAnnouncements.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-[100]">
          {bannerAnnouncements.map(announcement => {
            const config = TYPE_CONFIG[announcement.type] || TYPE_CONFIG.info;
            const Icon = config.icon;

            return (
              <div
                key={announcement.id}
                className={`${config.bgColor} ${config.textColor} px-4 py-2 shadow-lg`}
              >
                <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Icon className="w-5 h-5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold mr-2">{announcement.title}:</span>
                      <span className="opacity-90">{announcement.message}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`${config.textColor} hover:bg-white/20 shrink-0 h-8 w-8`}
                    onClick={() => dismissBanner(announcement.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Blocking Modal */}
      {blockingAnnouncement && (
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent
            className="sm:max-w-md"
            hideCloseButton
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <div className="flex justify-center mb-4">
                {(() => {
                  const config = TYPE_CONFIG[blockingAnnouncement.type] || TYPE_CONFIG.info;
                  const Icon = config.icon;
                  return (
                    <div className={`w-16 h-16 rounded-full ${config.bgColor} flex items-center justify-center`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  );
                })()}
              </div>
              <DialogTitle className="text-center text-xl">
                {blockingAnnouncement.title}
              </DialogTitle>
              <DialogDescription className="text-center text-base mt-2">
                {blockingAnnouncement.message}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6">
              <Button
                onClick={() => acknowledgeBlocking(blockingAnnouncement.id)}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600"
              >
                Rozumiem, kontynuuj
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
